import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { djs } from '@duke-hq/djs';
import {
  ContractTypes,
  CourseSegment,
  CourseSegmentOptions,
  WorkSchedule,
  getCategoryOptions,
  SkillSetContractCategories,
  isCourseSegment,
  ContractTypesInterface,
  CategoryOptionsInterface,
  CourseSegmentOptionsInterface,
  isSkillSetContractCategory,
} from '@duke-hq/shared-constants/enrolments';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Select } from 'chakra-react-select';
import { AlertCircleIcon } from 'lucide-react';
import { useEffect, useMemo, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSemesterEndDate } from '@duke-hq/ui/components/hooks/useSemesterEndDate';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import { skipToken } from '@tanstack/react-query';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { getCourseDatesFromAdminCourseSchedule } from '@duke-hq/lib/dates';
import { courseHasSemesterTwo } from '@duke-hq/shared-constants/course';
import { SemesterFreeField } from './SemesterFreeField';

export interface EnrolmentSettingsDataTypes {
  semester_2_start_at_date?: string;
  start_at_date: string; // Must be in 'YYYY-MM-DD' format
  enrolment_contract_type: string;
  contract_category: string;
  work_schedule: WorkSchedule;
  course_segment: CourseSegment;
  tenant_id?: number;
  consultant_id?: string;
  site_location_id?: string;
  externalId?: string;
  semesterTwoExternalId?: string; // Must be in 'YYYY-MM-DD' format
  isComplimentary: boolean;
  sem1IsFree: boolean;
  sem2IsFree: boolean;
  sem1FreeReason?: string;
  sem2FreeReason?: string;
}

const skillSetCategories = [
  SkillSetContractCategories.STUDENT,
  SkillSetContractCategories.INTERNAL_ENROLMENTS,
];

export function EnrolmentForm({
  enrolment,
}: {
  enrolment?: NonNullable<RouterOutputs['enrolment']['byIdWithResourceStatus']>;
}) {
  const { data: courseSchedule, isLoading } = trpcAdmin.courses.courseSchedule.useQuery(
    enrolment?.course?.id ?? skipToken,
  );

  const {
    register,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors, defaultValues },
  } = useFormContext<EnrolmentSettingsDataTypes>();

  const [
    startAtDate,
    workSchedule,
    enrolmentContractType,
    courseSegment,
    contractCategory,
    externalId,
    semesterTwoExternalId,
    semester2StartAtDate,
    sem1IsFree,
    sem2IsFree,
  ] = watch([
    'start_at_date',
    'work_schedule',
    'enrolment_contract_type',
    'course_segment',
    'contract_category',
    'externalId',
    'semesterTwoExternalId',
    'semester_2_start_at_date',
    'sem1IsFree',
    'sem2IsFree',
    'isComplimentary',
  ]);

  const today = djs();

  const categoryOptions = useMemo(() => {
    if (enrolmentContractType === 'Other') {
      return [
        {
          label: 'Internal enrolment',
          value: SkillSetContractCategories.INTERNAL_ENROLMENTS,
        },
        {
          label: 'Regular enrolment',
          value: 'n/a',
        },
      ];
    } else {
      return getCategoryOptions(enrolmentContractType);
    }
  }, [enrolmentContractType]);

  const fieldsThatAlterValues = ['start_at_date', 'work_schedule'] as const;
  const valuesThatAlterSchedule = watch(fieldsThatAlterValues);
  const hasAlteredValueThatImpactsSchedule = fieldsThatAlterValues.some((value, index) => {
    if (!defaultValues) {
      return false;
    }
    return defaultValues[value] !== valuesThatAlterSchedule[index];
  });

  const semester2MinStartDate = useSemesterEndDate({
    workSchedule,
    startAt: startAtDate,
  });

  const courseStartAndEndDates = useMemo(() => {
    if (!courseSchedule || isLoading) {
      return null;
    }

    try {
      return getCourseDatesFromAdminCourseSchedule({
        adminCourseScheduleItems: courseSchedule.schedule,
        courseSegment: courseSegment ?? CourseSegment.FULL_COURSE,
        workSchedule: workSchedule ?? WorkSchedule.FULL_TIME,
        startAt: startAtDate ?? today.toDate(),
        semesterTwoStartAt: semester2StartAtDate ?? null,
      });
    } catch {
      // Handle invalid user input in the date fields
      return null;
    }
  }, [
    courseSchedule,
    isLoading,
    courseSegment,
    workSchedule,
    startAtDate,
    today,
    semester2StartAtDate,
  ]);

  const canUpdateSemesterTwo = useMemo(() => {
    if (enrolment?.course?.kind !== CourseKindSchema.enum.QUALIFICATION) {
      return false;
    }

    if (enrolment.course.semesterCount < 2) {
      return false;
    }

    return (
      (isCourseSegment(courseSegment) && courseSegment === CourseSegment.FULL_COURSE) ||
      (isSkillSetContractCategory(contractCategory) &&
        skillSetCategories.includes(contractCategory))
    );
  }, [enrolment?.course?.kind, enrolment?.course?.semesterCount, courseSegment, contractCategory]);

  // TODO: this doesn't need to be an effect
  useEffect(() => {
    if (
      isSkillSetContractCategory(contractCategory) &&
      skillSetCategories.includes(contractCategory)
    ) {
      setValue('semester_2_start_at_date', semester2MinStartDate.format('YYYY-MM-DD'));
    }

    if (isCourseSegment(courseSegment) && courseSegment !== CourseSegment.FULL_COURSE) {
      return;
    } else if (hasAlteredValueThatImpactsSchedule) {
      setValue('semester_2_start_at_date', semester2MinStartDate.format('YYYY-MM-DD'));
    } else {
      setValue('semester_2_start_at_date', defaultValues?.semester_2_start_at_date);
    }
  }, [
    hasAlteredValueThatImpactsSchedule,
    defaultValues,
    setValue,
    semester2MinStartDate,
    workSchedule,
    courseSegment,
    contractCategory,
  ]);

  const handleContractTypeChange = useCallback(
    (selectedContractType: string) => {
      setValue('enrolment_contract_type', selectedContractType);

      // Default logic for all contract types
      setValue('course_segment', CourseSegment.FULL_COURSE);

      // Overwrite defaults for specific contract types
      if (selectedContractType === 'Workforce Australia') {
        if (enrolment?.course?.kind === CourseKindSchema.enum.QUALIFICATION) {
          setValue('course_segment', CourseSegment.FIRST_SEMESTER_ONLY);
        }
      } else if (selectedContractType === 'IEA') {
        // IEA requires full-time enrolment pace
        setValue('work_schedule', WorkSchedule.FULL_TIME);
      }

      const newCategoryOptions = getCategoryOptions(selectedContractType);

      if (newCategoryOptions.length === 1) {
        const [option] = newCategoryOptions;
        if (option) {
          setValue('contract_category', option.value);
        }
      } else if (newCategoryOptions.length < 1) {
        setValue('contract_category', 'n/a');
      } else {
        setValue('contract_category', '');
      }
    },
    [enrolment?.course?.kind, setValue],
  );

  const hasSemesterTwo = courseHasSemesterTwo(courseSchedule?.schedule ?? []);

  // Determine if we should show semester-specific free fields or the full complimentary flag
  // Show semester fields for multi-semester qualifications
  // Everything else uses the complimentary flag
  const shouldShowSemesterFields = useMemo(() => {
    // Must be a qualification
    if (enrolment?.course?.kind !== CourseKindSchema.enum.QUALIFICATION) {
      return false;
    }
    // Must have multiple semesters
    if ((enrolment.course.semesterCount ?? 0) < 2) {
      return false;
    }
    // Show for full course enrolments OR first semester only enrolments (where sem 2 checkbox will be disabled)
    return (
      courseSegment === CourseSegment.FULL_COURSE ||
      courseSegment === CourseSegment.FIRST_SEMESTER_ONLY
    );
  }, [enrolment?.course?.kind, enrolment?.course?.semesterCount, courseSegment]);

  return (
    <VStack gap="16px" w="full" p="8px">
      <VStack w="full" justify="space-between" align="start" gap="8px">
        <FormControl isInvalid={!!errors.start_at_date} isRequired>
          <FormLabel htmlFor="start_at_date">
            <Trans>Start date</Trans>
          </FormLabel>
          <Input
            id="start_at_date"
            {...register('start_at_date', {
              required: t`Start date is required.`,
              validate: (value) => djs(value).isValid() || t`Please enter a valid date.`,
            })}
            type="date"
            defaultValue={djs(today).format('YYYY-MM-DD')}
          />
          <FormErrorMessage>{errors.start_at_date?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.externalId}>
          <FormLabel htmlFor="externalId">
            <Trans>Purchase order (or unique identifier)</Trans>
          </FormLabel>
          <Input
            id="externalId"
            {...register('externalId')}
            spellCheck="false"
            type="text"
            defaultValue={externalId}
          />
          <FormErrorMessage>{errors.externalId?.message}</FormErrorMessage>
        </FormControl>
      </VStack>

      <HStack w="full" justify="space-between" align="start" gap="16px">
        <FormControl isInvalid={!!errors.enrolment_contract_type} isRequired>
          <FormLabel htmlFor="enrolment_contract_type">
            <Trans>Contract type</Trans>
          </FormLabel>
          <Controller
            name="enrolment_contract_type"
            control={control}
            rules={{ required: t`Contract type is required.` }}
            render={({ field }) => (
              <Select
                id="enrolment_contract_type"
                placeholder={t`Select option`}
                options={ContractTypes}
                value={ContractTypes.find((option) => option.value === field.value)}
                onChange={(val: ContractTypesInterface) => {
                  if (val == null || val.value === field.value) {
                    return;
                  }

                  handleContractTypeChange(val.value);
                }}
              />
            )}
          />
          <FormErrorMessage>{errors.enrolment_contract_type?.message}</FormErrorMessage>
        </FormControl>
        {categoryOptions.length > 0 && (
          <FormControl
            isInvalid={!!errors.contract_category}
            isRequired
            isDisabled={categoryOptions.length < 1}
          >
            <FormLabel htmlFor="contract_category">
              <Trans>Contract category</Trans>
            </FormLabel>
            <Controller
              name="contract_category"
              control={control}
              rules={{
                required: categoryOptions.length > 0 ? t`Contract category is required.` : false,
                validate: {
                  hasValue: (value) => {
                    if (categoryOptions.length === 0) {
                      return true;
                    }
                    return !!value || t`Please select a contract category.`;
                  },
                },
              }}
              render={({ field }) => {
                return (
                  <Select
                    key={getValues('contract_category')}
                    id="contract_category"
                    placeholder={t`Select option`}
                    options={categoryOptions}
                    value={categoryOptions.find((option) => option.value === field.value)}
                    onChange={(val: CategoryOptionsInterface) => field.onChange(val?.value)}
                    isReadOnly={categoryOptions.length === 1}
                  />
                );
              }}
            />
            <FormErrorMessage>{errors.contract_category?.message}</FormErrorMessage>
          </FormControl>
        )}
      </HStack>

      <HStack w="full" justify="space-between" align="start" gap="10px">
        {hasSemesterTwo ? (
          <FormControl isInvalid={!!errors.course_segment} isRequired>
            <FormLabel htmlFor="course_segment">
              <Trans>Course segment</Trans>
            </FormLabel>
            <Controller
              name="course_segment"
              control={control}
              rules={{ required: t`Course segment is required.` }}
              render={({ field }) => (
                <Select
                  id="course_segment"
                  placeholder={t`Select option`}
                  options={CourseSegmentOptions}
                  value={CourseSegmentOptions.find(
                    (option) => isCourseSegment(field.value) && option.value === field.value,
                  )}
                  isDisabled={enrolmentContractType === 'IEA'}
                  onChange={(val: CourseSegmentOptionsInterface) => field.onChange(val?.value)}
                />
              )}
            />
            <FormErrorMessage>{errors.course_segment?.message}</FormErrorMessage>
          </FormControl>
        ) : null}

        <FormControl isInvalid={!!errors.work_schedule} isRequired>
          <FormLabel>
            <Trans>Enrolment type</Trans>
          </FormLabel>
          <Controller
            name="work_schedule"
            control={control}
            rules={{ required: t`Enrolment type is required.` }}
            render={({ field }) => (
              <HStack align="center">
                <ButtonGroup spacing={0} isAttached>
                  <Button
                    colorScheme={field.value === WorkSchedule.FULL_TIME ? 'brand' : 'gray'}
                    onClick={() => {
                      field.onChange(WorkSchedule.FULL_TIME);
                    }}
                    variant={field.value === WorkSchedule.FULL_TIME ? 'solid' : 'outline'}
                    isDisabled={enrolmentContractType === 'IEA' || !enrolmentContractType}
                  >
                    <Trans>Full-time</Trans>
                  </Button>
                  <Button
                    colorScheme={field.value === WorkSchedule.PART_TIME ? 'brand' : 'gray'}
                    onClick={() => {
                      field.onChange(WorkSchedule.PART_TIME);
                    }}
                    variant={field.value === WorkSchedule.PART_TIME ? 'solid' : 'outline'}
                    isDisabled={enrolmentContractType === 'IEA' || !enrolmentContractType}
                  >
                    <Trans>Part-time</Trans>
                  </Button>
                </ButtonGroup>
                {field.value === WorkSchedule.PART_TIME && (
                  <Tooltip
                    label={
                      <Box
                        bg="bg.surface"
                        borderRadius="8px"
                        color="fg.default"
                        borderWidth="1px"
                        borderColor="border.default"
                        p="8px"
                        fontSize="sm"
                      >
                        <Trans>
                          Please note, by selecting part-time no outcome can be claimed.
                        </Trans>
                      </Box>
                    }
                    fontSize="md"
                    colorScheme="warning"
                    placement="bottom"
                    gutter={16}
                    bg="transparent"
                    shadow="none"
                  >
                    <Icon as={AlertCircleIcon} color="warning.emphasis" boxSize="20px" />
                  </Tooltip>
                )}
              </HStack>
            )}
          />
          <FormErrorMessage>{errors.work_schedule?.message}</FormErrorMessage>
        </FormControl>
      </HStack>

      {/* Semester 2 Section */}
      {canUpdateSemesterTwo && semester2MinStartDate != null ? (
        <VStack
          w="full"
          align="start"
          gap="8px"
          pt="8px"
          borderTop="1px"
          borderColor="border.default"
        >
          <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
            <Trans>Semester 2</Trans>
          </Text>
          <VStack w="full" justify="space-between" align="start" gap="8px">
            <FormControl isInvalid={!!errors.semester_2_start_at_date} isRequired>
              <FormLabel htmlFor="semester_2_start_at_date">
                <Trans>Semester 2 Start date</Trans>
              </FormLabel>
              <Input
                id="semester_2_start_at_date"
                {...register('semester_2_start_at_date', {
                  required: canUpdateSemesterTwo ? t`Semester 2 Start date is required.` : false,
                  validate: {
                    validDate: (value) => {
                      if (!canUpdateSemesterTwo) {
                        return true;
                      }
                      return djs(value).isValid() || t`Please enter a valid date.`;
                    },
                    afterSemester1: (value) => {
                      if (!canUpdateSemesterTwo || !semester2MinStartDate.isValid()) {
                        return true;
                      }
                      return (
                        djs(value).isSameOrAfter(semester2MinStartDate, 'day') ||
                        t`Semester 2 must start after Semester 1 ends (${semester2MinStartDate.format('DD/MM/YYYY')}).`
                      );
                    },
                  },
                })}
                type="date"
                min={semester2MinStartDate.format('YYYY-MM-DD')}
              />
              <FormErrorMessage>{errors.semester_2_start_at_date?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.semesterTwoExternalId}>
              <FormLabel htmlFor="semesterTwoExternalId">
                <Trans>Purchase order for Semester 2</Trans>
              </FormLabel>
              <Input
                id="semesterTwoExternalId"
                {...register('semesterTwoExternalId')}
                spellCheck="false"
                type="text"
                defaultValue={semesterTwoExternalId}
              />
              <FormErrorMessage>{errors.semesterTwoExternalId?.message}</FormErrorMessage>
            </FormControl>
          </VStack>
        </VStack>
      ) : null}

      {courseStartAndEndDates?.courseEndDate ? (
        <Text color="fg.emphasis" textStyle="sm" textAlign="left" fontWeight="normal" w="full">
          <Trans>Course is expected to end by</Trans> {courseStartAndEndDates.courseEndDate.long}
        </Text>
      ) : null}

      {/* Course enrolment financial status */}
      <VStack
        w="full"
        align="start"
        gap="8px"
        pt="8px"
        borderTop="1px"
        borderColor="border.default"
      >
        <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
          <Trans>Finances</Trans>
        </Text>
        <VStack w="full" justify="space-between" align="start" gap="8px">
          {/* For full course enrolments in multi-semester qualifications: show semester-specific free fields */}
          {shouldShowSemesterFields ? (
            <VStack w="full" align="start" gap="8px">
              <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
                <Trans>Invoice adjustments:</Trans>
              </Text>
              <Text fontSize="xs" color="fg.subtle">
                <Trans>
                  This qualification has multiple semesters — invoicing can be waived per semester.
                </Trans>
              </Text>

              <SemesterFreeField
                semester={1}
                control={control}
                register={register}
                isFree={sem1IsFree}
                isDisabled={false}
                errors={errors}
              />

              <SemesterFreeField
                semester={2}
                control={control}
                register={register}
                isFree={sem2IsFree}
                isDisabled={courseSegment === CourseSegment.FIRST_SEMESTER_ONLY}
                errors={errors}
              />
            </VStack>
          ) : (
            /* For all other cases: show full complimentary flag */
            <VStack w="full" align="start" gap={2}>
              <Text fontSize="xs" color="fg.subtle">
                <Trans>
                  This course has a single semester. Marking it complimentary will waive the invoice
                  for the entire enrolment.
                </Trans>
              </Text>
              <FormControl>
                <Controller
                  name="isComplimentary"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="isComplimentary"
                      isChecked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      defaultChecked={enrolment?.isComplimentary ?? false}
                    >
                      <Trans>Complimentary enrolment (no invoice will be generated)</Trans>
                    </Checkbox>
                  )}
                />
              </FormControl>
            </VStack>
          )}
        </VStack>
      </VStack>
    </VStack>
  );
}
